/* eslint-disable max-lines */
/**
 * Centralized React Query key definitions
 *
 * This module provides type-safe query keys for all React Query operations.
 * Using centralized keys ensures consistent cache invalidation and prevents
 * typos in query key strings.
 *
 * @module query-keys
 *
 * @example
 * // Using query keys
 * import { queryKeys } from '@/lib/query-keys';
 *
 * const clientsQuery = useQuery({
 *   queryKey: queryKeys.clients.all,
 *   queryFn: fetchClients,
 * });
 *
 * // Invalidating related queries
 * queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
 * queryClient.invalidateQueries({ queryKey: queryKeys.clients.detail(clientId) });
 */
import { queryKeysExtended } from './query-keys.extended';

/**
 * Query key factory functions organized by domain
 */
export const queryKeys = {
  // =========================================================================
  // Clients
  // =========================================================================
  clients: {
    /** All clients list */
    all: ['clients'] as const,
    /** Clients list scoped by organization */
    list: (orgId?: string) => ['clients', orgId] as const,
    /** Single client by ID */
    detail: (id?: string) => ['client', id] as const,
    /** Client health widget data */
    healthWidget: ['client-health-widget-clients'] as const,
    /** Client health contracts */
    healthContracts: ['client-health-widget-contracts'] as const,
    /** Client team members */
    team: (clientId?: string) => ['client-team', clientId] as const,
    /** Client activity insights */
    activityInsights: (clientId?: string) => ['client-activity-insights', clientId] as const,
    /** Client goals */
    goals: (clientId?: string) => ['client-goals', clientId] as const,
    /** Client team members (legacy key) */
    teamMembers: (clientId?: string) => ['team-members', clientId] as const,
    /** Client team members (full) */
    clientTeamMembers: (clientId?: string) => ['client-team-members', clientId] as const,
    /** Client attention obligations */
    attentionObligations: (clientId?: string) =>
      ['client-attention-obligations', clientId] as const,
    /** Client meetings needing transcripts */
    attentionMeetingsNeedingTranscripts: (clientId?: string) =>
      ['attention', 'meetings-needing-transcripts', clientId] as const,
    /** Client emails needing response */
    attentionEmailsNeedingResponse: (clientId?: string) =>
      ['attention', 'emails-needing-response', clientId] as const,
    /** Client contact suggestions */
    contactSuggestions: (clientId?: string) => ['client-contact-suggestions', clientId] as const,
    /** Contact email subjects preview */
    contactEmailSubjects: (clientId?: string, email?: string) =>
      ['contact-email-subjects', clientId, email] as const,
    /** Client financial audit log */
    financialAuditLog: (clientId?: string) => ['client-financial-audit-log', clientId] as const,
  },

  // =========================================================================
  // Presets
  // =========================================================================
  presets: {
    /** Base key for all preset response queries */
    all: ['preset-responses'] as const,
    /** Preset list by organization and optional filters */
    list: (organizationId?: string, options?: Record<string, unknown>) =>
      ['preset-responses', organizationId, options] as const,
    /** Single preset by ID */
    detail: (presetId?: string) => ['preset-responses', presetId] as const,
    /** Preset categories by organization */
    categories: (organizationId?: string) =>
      ['preset-responses', 'categories', organizationId] as const,
  },

  // =========================================================================
  // Projects (legacy keys for backward compatibility)
  // =========================================================================
  projects: {
    /** All projects */
    all: ['projects'] as const,
    /** Projects by client */
    byClient: (clientId?: string) => ['projects', clientId] as const,
    /** Client projects (alternative key) */
    clientProjects: (clientId?: string) => ['client-projects', clientId] as const,
    /** Single project by ID */
    detail: (id?: string) => ['project', id] as const,
    /** Latest project for a client */
    latest: (clientId?: string) => ['latest-client-project', clientId] as const,
    /** Project health snapshots */
    healthSnapshots: (projectId?: string) => ['project-health-snapshots', projectId] as const,
  },

  // =========================================================================
  // Projects V2 (live ClickUp integration)
  // =========================================================================
  projectsV2: {
    /** All projects V2 */
    all: ['projects-v2'] as const,
    /** Projects V2 by client */
    byClient: (clientId?: string) => ['projects-v2', clientId] as const,
    /** Single project V2 */
    detail: (projectId?: string) => ['project-v2', projectId] as const,
    /** Project tasks */
    tasks: (projectId?: string) => ['project-tasks', projectId] as const,
    /** Client overdue tasks */
    overdueTasks: (clientId?: string) => ['client-overdue-tasks', clientId] as const,
    /** Project health history */
    healthHistory: (projectId?: string, weeks?: number) =>
      ['project-health-history', projectId, weeks] as const,
    /** Projects health trend */
    healthTrend: (weeks?: number) => ['projects-health-trend', weeks] as const,
    /** Projects dashboard summary */
    dashboard: ['projects-dashboard'] as const,
    /** Project linked contracts */
    contracts: (projectId?: string) => ['project-contracts', projectId] as const,
  },

  // =========================================================================
  // Meetings & Analysis
  // =========================================================================
  meetings: {
    /** All meetings */
    all: ['all-meetings'] as const,
    /** Meetings/analyses by client */
    byClient: (clientId: string) => ['analyses', clientId] as const,
    /** Single meeting/analysis */
    detail: (id: string) => ['analysis', id] as const,
    /** Meeting scripts */
    scripts: ['meeting-scripts'] as const,
    /** Meeting scripts by client */
    scriptsByClient: (clientId?: string) => ['meeting-scripts', clientId] as const,
    /** Meeting script by ID */
    script: (id?: string) => ['meeting-script', id] as const,
    /** Meeting types */
    types: ['meeting-types'] as const,
    /** Single meeting type */
    type: (id?: string | null) => ['meeting-type', id] as const,
  },

  // =========================================================================
  // Analyses (alias for meetings, used in some components)
  // =========================================================================
  analyses: {
    /** All analyses */
    all: ['analyses'] as const,
    /** Single analysis by ID */
    detail: (id?: string) => ['analysis', id] as const,
    /** Analyses by client */
    byClient: (clientId?: string) => ['analyses', clientId] as const,
    /** Most recent analysis for a client */
    recentByClient: (clientId?: string) => ['recent-analysis', clientId] as const,
    /** Analysis counts per client */
    counts: () => ['analyses-counts'] as const,
    /** Auto-generated analyses pending human review */
    pendingReviews: (orgId?: string) => ['analyses', 'pending-reviews', orgId] as const,
  },

  // =========================================================================
  // Agreements
  // =========================================================================
  agreements: {
    /** Organization settings */
    orgSettings: ['agreement-org-settings'] as const,
    /** All templates */
    templates: ['agreement-templates'] as const,
    /** Templates by type */
    templatesByType: (type: string) => ['agreement-templates', type] as const,
    /** Single template */
    template: (id?: string) => ['agreement-template', id] as const,
    /** Client agreements with filters */
    list: (filters?: Record<string, unknown>) => ['client-agreements', filters] as const,
    /** All client agreements */
    all: ['client-agreements'] as const,
    /** Single agreement */
    detail: (id?: string) => ['client-agreement', id] as const,
    /** Agreement audit log */
    auditLog: (agreementId?: string) => ['agreement-audit-log', agreementId] as const,
    /** Agreement deliverables */
    deliverables: (agreementId?: string) => ['agreement-deliverables', agreementId] as const,
    /** Agreement payment terms */
    paymentTerms: (agreementId?: string) => ['agreement-payment-terms', agreementId] as const,
    /** Agreement SLAs */
    slas: (agreementId?: string) => ['agreement-slas', agreementId] as const,
  },

  // =========================================================================
  // Contracts & Documents
  // =========================================================================
  contracts: {
    /** All contracts */
    all: ['contracts'] as const,
    /** Contracts by client */
    byClient: (clientId: string) => ['contracts', clientId] as const,
    /** Single contract */
    detail: (id: string) => ['contract', id] as const,
    /** Contract obligations */
    obligations: (contractId: string) => ['contract-obligations', contractId] as const,
    /** Contracts with obligations (for health aggregation) */
    withObligations: () => ['contracts-with-obligations'] as const,
    /** Legacy obligations list key */
    obligationsList: ['obligations'] as const,
    /** Legacy obligations count key */
    obligationsCount: ['obligations-count'] as const,
    /** Legacy documents list key */
    documents: ['documents'] as const,
    /** Legacy documents count key */
    documentsCount: ['documents-count'] as const,
    /** Legacy contractual_obligations key */
    contractualObligations: ['contractual_obligations'] as const,
    /** Legacy contractual-obligations key by client */
    contractualObligationsByClient: (clientId?: string) =>
      ['contractual-obligations', clientId] as const,
    /** Legacy documents-with-obligations key */
    documentsWithObligations: ['documents-with-obligations'] as const,
    /** Legacy client-documents-with-obligations key */
    clientDocumentsWithObligations: (clientId?: string) =>
      ['client-documents-with-obligations', clientId] as const,
    /** Legacy client-contracts-with-obligations base key */
    clientContractsWithObligationsBase: ['client-contracts-with-obligations'] as const,
    /** Legacy client-contracts-with-obligations key */
    clientContractsWithObligations: (clientId?: string) =>
      ['client-contracts-with-obligations', clientId] as const,
    /** Available contracts by client for project linking */
    availableByClient: (clientId?: string) => ['available-contracts', clientId] as const,
    /** Verbal agreements by client */
    verbalAgreements: (clientId?: string) => ['verbal-agreements', clientId] as const,
    /** Analysis agreements */
    analysisAgreements: (analysisId?: string) => ['analysis-agreements', analysisId] as const,
    /** Gold standards list */
    goldStandards: ['gold-standards'] as const,
    /** Single gold standard */
    goldStandardDetail: (id?: string) => ['gold-standard', id] as const,
    /** Gold standard versions */
    goldStandardVersions: (id?: string) => ['gold-standard-versions', id] as const,
    /** Gold standard by document type */
    goldStandardByType: (documentType?: string) => ['gold-standard-by-type', documentType] as const,
    /** Document compliance results */
    documentCompliance: (documentId?: string) => ['document-compliance', documentId] as const,
  },

  // =========================================================================
  // Goals
  // =========================================================================
  goals: {
    /** Goals by client */
    byClient: (clientId?: string) => ['client-goals', clientId] as const,
    /** Goals summary by client */
    summary: (clientId?: string) => ['client-goals-summary', clientId] as const,
    /** Suggested goals by client */
    suggested: (clientId: string) => ['client-suggested-goals', clientId] as const,
    /** Suggested goals count */
    suggestedCount: ['suggested-goals-count'] as const,
    /** All goals with client data */
    allWithClients: ['all-goals-with-clients'] as const,
    /** All suggested goals table data */
    allSuggestedTable: ['all-suggested-goals'] as const,
    /** All suggested goals */
    allSuggested: ['suggested-goals'] as const,
  },

  // =========================================================================
  // Health Engine
  // =========================================================================
  healthEngine: {
    /** Health score for a single client */
    clientScore: (clientId?: string) => ['health-engine', 'client', clientId] as const,
    /** Health scores for all clients (dashboard) */
    allScores: ['health-engine', 'all'] as const,
    /** Health alerts for a single client */
    clientAlerts: (clientId?: string) => ['health-engine', 'alerts', clientId] as const,
    /** Health alerts for all clients (dashboard feed) */
    allAlerts: ['health-engine', 'alerts'] as const,
    /** Health snapshots (trend data) */
    snapshots: (clientId?: string, weeks?: number) =>
      ['health-engine', 'snapshots', clientId, weeks] as const,
    /** Health engine configuration */
    config: ['health-engine', 'config'] as const,
    /** Account-owner pulse ratings */
    pulses: (clientId?: string) => ['health-engine', 'pulses', clientId] as const,
    /** All latest pulses (batch feed) */
    allPulses: ['health-engine', 'pulses', 'all'] as const,
  },

  // =========================================================================
  // Case Studies
  // =========================================================================
  caseStudies: {
    /** Case studies by client */
    byClient: (clientId?: string) => ['case-studies', clientId] as const,
  },

  // =========================================================================
  // Profile
  // =========================================================================
  profile: {
    /** Meeting prep notification preferences by user */
    meetingPrepNotifications: (userId?: string) => ['meeting-prep-notifications', userId] as const,
  },

  // =========================================================================
  // Invoices
  // =========================================================================
  invoices: {
    /** All invoices (base key for invalidation) */
    all: ['invoices'] as const,
    /** Invoices with organization/status filters */
    list: (organizationId?: string, status?: string) =>
      ['invoices', organizationId, status] as const,
    /** Invoices for a specific client */
    byClient: (clientId?: string) => ['client-invoices', clientId] as const,
    /** Single invoice by ID */
    detail: (invoiceId?: string) => ['invoice', invoiceId] as const,
    /** Overdue invoices (base key for invalidation) */
    overdueBase: ['overdue-invoices'] as const,
    /** Overdue invoices for an organization */
    overdue: (organizationId?: string) => ['overdue-invoices', organizationId] as const,
    /** Invoice summary (base key for invalidation) */
    summaryBase: ['invoice-summary'] as const,
    /** Invoice summary for an organization */
    summary: (organizationId?: string) => ['invoice-summary', organizationId] as const,
  },

  // =========================================================================
  // Tasks
  // =========================================================================
  tasks: {
    /** All tasks */
    all: ['tasks'] as const,
    /** Tasks by client */
    byClient: (clientId: string) => ['tasks', clientId] as const,
    /** Single task */
    detail: (id: string) => ['task', id] as const,
    /** Task statuses */
    statuses: ['task-statuses'] as const,
    /** Task mentions in meetings */
    mentions: (clickupTaskId?: string) => ['task-mentions', clickupTaskId] as const,
    /** Task related data (obligations, mini-SOWs) */
    relatedData: (clickupTaskId?: string) => ['task-related-data', clickupTaskId] as const,
  },

  // =========================================================================
  // Mini-SOW
  // =========================================================================
  miniSow: {
    /** All mini-SOWs */
    all: ['mini-sows'] as const,
    /** Mini-SOWs with filters */
    list: (filters?: Record<string, unknown>) => ['mini-sows', filters] as const,
    /** Mini-SOWs by client */
    byClient: (clientId: string) => ['mini-sows', clientId] as const,
    /** Single mini-SOW */
    detailBase: ['mini-sow'] as const,
    detail: (id?: string) => ['mini-sow', id] as const,
    /** Mini-SOW settings */
    settings: ['mini-sow-settings'] as const,
    /** Mini-SOW org settings */
    orgSettings: (orgId?: string) => ['mini-sow-org-settings', orgId] as const,
    /** Mini-SOW approver settings */
    approverSettings: (orgId?: string) => ['approver-settings', orgId] as const,
    /** Mini-SOW org default approver */
    orgDefaultApprover: (orgId?: string) => ['org-default-approver', orgId] as const,
    /** Mini-SOW templates */
    templates: ['mini-sow-templates'] as const,
    /** Communication history */
    communicationHistory: (miniSowId: string) => ['mini-sow-communication', miniSowId] as const,
    /** Line item comments */
    comments: (miniSowId?: string, lineItemDescription?: string) =>
      ['mini-sow-comments', miniSowId, lineItemDescription] as const,
    /** Hourly rates for organization */
    hourlyRates: (orgId?: string) => ['hourly-rates', orgId] as const,
    /** Org default hourly rate */
    orgHourlyRate: (orgId?: string) => ['org-hourly-rate', orgId] as const,
    /** Client financial settings (org-wide or client-specific) */
    clientFinancialSettings: (filter?: string) => ['client-financial-settings', filter] as const,
    /** Send history detail */
    sendHistoryDetail: (miniSowId?: string) => ['mini-sow-send-history-detail', miniSowId] as const,
    /** Send history base */
    sendHistory: ['mini-sow-send-history'] as const,
    /** Linked emails */
    linkedEmails: (miniSowId?: string) => ['mini-sow-linked-emails', miniSowId] as const,
    /** My email suggestions (for current user) */
    myEmailSuggestions: ['my-email-suggestions'] as const,
    /** Client email suggestions */
    clientEmailSuggestions: (clientId?: string) => ['client-email-suggestions', clientId] as const,
    /** Mini SOW email suggestions */
    emailSuggestions: (miniSowId?: string) => ['mini-sow-email-suggestions', miniSowId] as const,
    /** Pending suggestions grouped by user */
    pendingSuggestionsByUser: (orgId?: string) => ['pending-suggestions-by-user', orgId] as const,
    /** Send history by client (for recipient recommendations) */
    sendHistoryByClient: (clientId?: string, orgId?: string) =>
      ['mini-sow-send-history', clientId, orgId] as const,
    /** Simple clients list for mini-SOW editors */
    clientsSimple: ['clients-simple'] as const,
    /** Client hourly rate for mini-SOW editors */
    clientHourlyRate: (clientId?: string) => ['client-hourly-rate', clientId] as const,
    /** Projects for smart linking */
    projectsForLinking: (clientId?: string) => ['projects-for-linking', clientId] as const,
    /** Obligations for smart linking */
    obligationsForLinking: (clientId?: string) => ['obligations-for-linking', clientId] as const,
    /** User email suggestions for review dashboard */
    userEmailSuggestions: (userId?: string | null) => ['user-email-suggestions', userId] as const,
    /** Recipient recommendations */
    recipientRecommendations: (clientId?: string, orgId?: string) =>
      ['mini-sow-recipient-recommendations', clientId, orgId] as const,
  },

  // =========================================================================
  // Budget & Financial
  // =========================================================================
  budget: {
    /** Legacy budget base key */
    all: ['budget'] as const,
    /** Budget dashboard (base key for invalidation) */
    dashboard: ['budget-dashboard'] as const,
    /** Budget dashboard with params */
    dashboardWithParams: (orgId?: string, fiscalYear?: number) =>
      ['budget-dashboard', orgId, fiscalYear] as const,
    /** Budget metrics (base key for invalidation) */
    metrics: ['budget-metrics'] as const,
    /** Budget metrics with params */
    metricsWithParams: (orgId?: string, month?: string) =>
      ['budget-metrics', orgId, month] as const,
    /** Budget year summary (base key for invalidation) */
    yearSummary: ['budget-year-summary'] as const,
    /** Budget year summary with params */
    yearSummaryWithParams: (orgId?: string, fiscalYear?: number) =>
      ['budget-year-summary', orgId, fiscalYear] as const,
    /** Budget rate analysis (base key for invalidation) */
    rateAnalysis: ['budget-rate-analysis'] as const,
    /** Budget rate analysis with params */
    rateAnalysisWithParams: (orgId?: string, month?: string) =>
      ['budget-rate-analysis', orgId, month] as const,
    /** Budget targets (base key for invalidation) */
    targets: ['budget-targets'] as const,
    /** Budget targets with params */
    targetsWithParams: (orgId?: string, versionId?: string | null, scenarioId?: string | null) =>
      ['budget-targets', orgId, versionId, scenarioId] as const,
    /** Budget versions (base key for invalidation) */
    versions: ['budget-versions'] as const,
    /** Budget versions with params */
    versionsWithParams: (orgId?: string, fiscalYear?: number) =>
      ['budget-versions', orgId, fiscalYear] as const,
    /** Active budget version */
    versionActive: (orgId?: string, fiscalYear?: number) =>
      ['budget-version-active', orgId, fiscalYear] as const,
    /** Budget scenarios (base key for invalidation) */
    scenarios: ['budget-scenarios'] as const,
    /** Budget scenarios with params */
    scenariosWithParams: (orgId?: string) => ['budget-scenarios', orgId] as const,
    /** Monthly actuals (base key for invalidation) */
    actuals: ['budget-actuals'] as const,
    /** Monthly actuals with params */
    actualsWithParams: (orgId?: string, startMonth?: string, endMonth?: string) =>
      ['budget-actuals', orgId, startMonth, endMonth] as const,
    /** Single month actual */
    actual: (orgId?: string, month?: string) => ['budget-actual', orgId, month] as const,
    /** COGS line items */
    cogsItems: ['cogs-items'] as const,
    /** OpEx line items */
    opexItems: ['opex-items'] as const,
    /** Legacy OpEx line items key */
    opexLineItems: ['opex-line-items'] as const,
    /** OpEx line items scoped by org and month */
    opexLineItemsWithParams: (orgId?: string, month?: string) =>
      ['opex-line-items', orgId, month] as const,
    /** OpEx audit log base key */
    opexAuditLog: ['opex-audit-log'] as const,
    /** OpEx audit log scoped by org and category filters */
    opexAuditLogWithParams: (
      orgId?: string,
      includeCategories?: string[],
      excludeCategories?: string[],
    ) => ['opex-audit-log', orgId, includeCategories, excludeCategories] as const,
    /** Debt line items scoped by org and fiscal year */
    debtItems: (orgId?: string, fiscalYear?: number) => ['debt-items', orgId, fiscalYear] as const,
    /** Pipeline forecast (base key for invalidation) */
    pipeline: ['pipeline-forecast'] as const,
    /** Pipeline forecast with params */
    pipelineWithParams: (orgId?: string) => ['pipeline-forecast', orgId] as const,
    /** Pipeline forecast for month */
    pipelineForMonth: (orgId?: string, month?: string) =>
      ['pipeline-forecast-month', orgId, month] as const,
    /** Month close status */
    monthCloseStatus: (orgId?: string, month?: string) =>
      ['month-close-status', orgId, month] as const,
    /** Client financial settings */
    clientFinancials: (clientId: string) => ['client-financial-settings', clientId] as const,
    /** Client financial settings base key */
    clientFinancialSettings: ['client-financial-settings'] as const,
    /** Client financial settings scoped by organization */
    clientFinancialSettingsByOrg: (orgId?: string) => ['client-financial-settings', orgId] as const,
    /** All client financials base key */
    allClientFinancials: ['all-client-financials'] as const,
    /** All client financials scoped by organization */
    allClientFinancialsByOrg: (orgId?: string) => ['all-client-financials', orgId] as const,
    /** Contractor rates by organization */
    contractorRates: (orgId?: string) => ['contractor-rates', orgId] as const,
    /** Single contractor rate */
    contractorRate: (orgPersonId?: string) => ['contractor-rate', orgPersonId] as const,
  },

  // =========================================================================
  // ClickUp Integration
  // =========================================================================
  clickup: {
    /** ClickUp connection status */
    connection: ['clickup-connection'] as const,
    /** ClickUp token status */
    tokenStatus: ['clickup-token-status'] as const,
    /** ClickUp workspaces */
    workspaces: ['clickup-workspaces'] as const,
    /** ClickUp spaces by workspace */
    spaces: (workspaceId: string) => ['clickup-spaces', workspaceId] as const,
    /** ClickUp lists by space */
    lists: (spaceId: string) => ['clickup-lists', spaceId] as const,
    /** ClickUp folder lists */
    folderLists: (folderId: string) => ['clickup-folder-lists', folderId] as const,
    /** ClickUp team members */
    teamMembers: ['clickup-team-members'] as const,
    /** ClickUp statuses by space/list */
    statuses: (spaceId?: string | null, listId?: string | null) =>
      ['clickup-statuses', spaceId, listId] as const,
    /** ClickUp list statuses by list ID */
    listStatuses: (listId?: string | null) => ['clickup-list-statuses', listId] as const,
    /** ClickUp list members */
    listMembers: (listId?: string | null) => ['clickup-list-members', listId] as const,
    /** Accessible client IDs */
    accessibleClients: ['accessible-client-ids'] as const,
    /** Client access check */
    clientAccess: (clientId: string) => ['client-access', clientId] as const,
    /** Synced tasks (base key for invalidation) */
    syncedTasks: ['synced-tasks'] as const,
    /** Synced tasks by client */
    syncedTasksByClient: (clientId?: string | null) => ['synced-tasks', clientId] as const,
    /** Single synced task by ClickUp ID */
    syncedTask: (clickupTaskId?: string | null) => ['synced-task', clickupTaskId] as const,
    /** Sync status */
    syncStatus: ['sync-status'] as const,
    /** ClickUp webhooks */
    webhooks: ['clickup-webhooks'] as const,
    /** Status mappings */
    statusMappings: ['clickup-status-mappings'] as const,
    /** Overdue ClickUp tasks */
    tasksDue: (orgId?: string, daysOverdue?: number) =>
      ['clickup-tasks-due', orgId, daysOverdue] as const,
  },

  // =========================================================================
  // Google Workspace
  // =========================================================================
  google: {
    /** Gmail connection status (single/primary - backwards compat) */
    gmailConnection: ['gmail-connection'] as const,
    /** All Gmail connections for current user */
    gmailConnections: ['gmail-connections'] as const,
    /** Drive connection status */
    driveConnection: ['google-drive-connection'] as const,
    /** Gmail emails */
    emails: (clientId?: string) => ['gmail-emails', clientId] as const,
    /** Drive folders */
    folders: ['drive-folders'] as const,
    /** Drive folders by parent */
    driveFolders: (parentFolderId?: string | null) =>
      ['google-drive-folders', parentFolderId] as const,
    /** Shared drive folders */
    sharedDriveFolders: (driveId?: string | null, parentFolderId?: string | null) =>
      ['shared-drive-folders', driveId, parentFolderId] as const,
    /** Client Drive folder mapping */
    clientDriveFolder: (clientId?: string) => ['client-drive-folder', clientId] as const,
    /** Watched folders */
    watchedFolders: ['watched-folders'] as const,
    /** Drive files */
    files: (folderId: string) => ['drive-files', folderId] as const,
    /** Google Workspace connection */
    workspaceConnection: (orgId?: string) => ['google-workspace-connection', orgId] as const,
    /** Client email domains */
    clientEmailDomains: (clientId?: string) => ['client-email-domains', clientId] as const,
    /** All email domains for organization */
    allEmailDomains: (orgId?: string) => ['all-email-domains', orgId] as const,
    /** Client emails */
    clientEmails: (clientId?: string, options?: Record<string, unknown>) =>
      ['client-emails', clientId, options] as const,
    /** Single email */
    emailDetail: (emailId?: string) => ['email', emailId] as const,
    /** Email thread */
    emailThread: (threadId?: string, clientId?: string) =>
      ['email-thread', threadId, clientId] as const,
    /** Search emails */
    searchEmails: (clientId: string, searchQuery: string) =>
      ['search-emails', clientId, searchQuery] as const,
    /** Email attachments */
    emailAttachments: (emailId?: string) => ['email-attachments', emailId] as const,
    /** Sync logs */
    syncLogs: (orgId?: string, limit?: number) => ['sync-logs', orgId, limit] as const,
    /** Email stats */
    emailStats: (id?: string) => ['email-stats', id] as const,
    /** Last Gmail scan time */
    lastScan: ['last-gmail-scan'] as const,
  },

  // =========================================================================
  // Slack Integration
  // =========================================================================
  slack: {
    /** Slack connection status */
    connection: (orgId?: string) => ['slack-connection', orgId] as const,
    /** Slack channels */
    channels: ['slack-channels'] as const,
    /** Slack channel mappings */
    channelMappings: (clientId?: string) => ['slack-channel-mappings', clientId] as const,
    /** Slack messages */
    messages: (clientId?: string, limit?: number) => ['slack-messages', clientId, limit] as const,
    /** Left on read status */
    leftOnRead: (clientId?: string) => ['slack-left-on-read', clientId] as const,
    /** Left on read summary */
    leftOnReadSummary: ['slack-left-on-read-summary'] as const,
    /** Slack participants for organization */
    participants: (orgId?: string) => ['slack-participants', orgId] as const,
    /** Internal chat messages for sidebar */
    internalChat: (channelMappingId?: string) =>
      ['slack', 'internal-chat', channelMappingId] as const,
    /** Thread replies for internal chat */
    threadReplies: (channelMappingId?: string, threadTs?: string) =>
      ['slack', 'thread-replies', channelMappingId, threadTs] as const,
  },

  // =========================================================================
  // Email
  // =========================================================================
  email: {
    /** Weekly summaries */
    weeklySummaries: (clientId: string) => ['weekly-summaries', clientId] as const,
    /** Weekly summary history */
    weeklySummaryHistory: (clientId?: string) => ['weekly-summary-history', clientId] as const,
    /** All weekly summaries (for manager dashboard) */
    allSummaries: (options?: Record<string, unknown>) => ['all-weekly-summaries', options] as const,
    /** Overdue weekly summaries */
    overdueSummaries: ['overdue-weekly-summaries'] as const,
    /** Overdue weekly summaries with threshold */
    overdueSummariesWithThreshold: (daysThreshold?: number) =>
      ['overdue-weekly-summaries', daysThreshold] as const,
    /** Weekly recipient recommendations */
    weeklyRecipientRecs: (clientId?: string) => ['recipient-recommendations', clientId] as const,
    /** Weekly followup skips */
    followupSkips: (clientId?: string, weeksBack?: number) =>
      ['weekly-followup-skips', clientId, weeksBack] as const,
    /** Current week followup skip */
    followupSkipCurrent: (clientId?: string) => ['weekly-followup-skip-current', clientId] as const,
    /** Meeting prep emails */
    meetingPrep: (clientId: string) => ['meeting-prep-emails', clientId] as const,
    /** Meeting prep history */
    meetingPrepHistory: (clientId?: string) => ['meeting-prep-history', clientId] as const,
    /** Meeting prep recipient recommendations */
    meetingPrepRecipientRecs: (clientId?: string) =>
      ['meeting-prep-recipient-recommendations', clientId] as const,
    /** Scheduled meetings for prep */
    scheduledMeetingsForPrep: (daysAhead?: number) =>
      ['scheduled-meetings-for-prep', daysAhead] as const,
    /** Email history */
    history: (clientId: string) => ['email-history', clientId] as const,
    /** Email recipients */
    recipients: (clientId: string) => ['email-recipients', clientId] as const,
    /** Email compositions */
    compositions: (clientId?: string, emailType?: string) =>
      ['email-compositions', clientId, emailType] as const,
    /** Unified recipient recommendations */
    recipientRecommendations: (clientId?: string, emailType?: string) =>
      ['recipient-recommendations-unified', clientId, emailType] as const,
    /** Unified recipient recommendations keyed by current email history size */
    recipientRecommendationsWithHistory: (
      clientId?: string,
      emailType?: string,
      historyLength?: number,
    ) => ['recipient-recommendations-unified', clientId, emailType, historyLength] as const,
  },

  ...queryKeysExtended,
} as const;

/**
 * Helper type to extract query key type for a specific domain
 */
export type QueryKeyOf<T extends keyof typeof queryKeys> = ReturnType<
  (typeof queryKeys)[T][keyof (typeof queryKeys)[T]]
>;

/**
 * Helper function to get all keys for a domain (for batch invalidation)
 *
 * @param domain - The domain to get keys for
 * @returns Array of query keys for the domain
 *
 * @example
 * // Invalidate all client-related queries
 * getQueryKeysForDomain('clients').forEach(key => {
 *   queryClient.invalidateQueries({ queryKey: key });
 * });
 */
export function getQueryKeysForDomain(domain: keyof typeof queryKeys): readonly string[][] {
  const domainKeys = queryKeys[domain];
  return Object.values(domainKeys)
    .filter((value): value is readonly string[] => Array.isArray(value))
    .map((key) => [...key] as string[]);
}

export default queryKeys;
